import { RiskFieldsTypeEnum, TemplateFieldTypeEnum } from '@/__generated__/graphql';
import { CustomField } from '@/components/shared/CustomField';
import { RiskFactorInputTabs } from '@/components/shared/RiskFactorInputTabs';
import Stack from '@mui/material/Stack';
import { useCallback, useState } from 'react';
import { TIMBER_FACING, UHMW_FACING } from '@/components/Analysis/RiskAssessment/constants';
import { textFieldStyles } from '../styles';

type Props = {
  onChange: ({
    pofValues,
    inputs,
  }: {
    pofValues?: { [key: string]: string };
    inputs?: {
      id: string;
      value: string;
    }[];
  }) => void;
};

export const FenderPanelFacingPoF = ({ onChange }: Props) => {
  const [selectedValues, setSelectedValues] = useState<{ [key: string]: string | null }>({
    risk_facing_type: UHMW_FACING,
    risk_pad_thickness: '',
    risk_timber_service_time: '',
    risk_cross_section_loss_in_area: '',
    risk_cracking_in_timber_fendering: '',
  });

  const changeField = (value: string, fieldId: string) => {
    setSelectedValues((current) => ({
      ...current,
      [fieldId]: value,
    }));
  };

  const changeRiskFactor = useCallback(
    (selectedRiskFactorValues: { [key: string]: string | null }) => {
      const inputValues = [
        { id: 'risk_base_pof_value', value: selectedValues.E ?? '' },
        { id: 'risk_facing_type', value: selectedValues.risk_facing_type ?? '' },
        { id: 'risk_pad_thickness', value: selectedValues.risk_pad_thickness ?? '' },
        { id: 'risk_timber_service_time', value: selectedValues.risk_timber_service_time ?? '' },
        {
          id: 'risk_cross_section_loss_in_area',
          value: selectedValues.risk_cross_section_loss_in_area ?? '',
        },
        {
          id: 'risk_cracking_in_timber_fendering',
          value: selectedValues.risk_cracking_in_timber_fendering ?? '',
        },
        {
          id: 'risk_safety_value_pof_c',
          value: selectedRiskFactorValues.risk_safety_calculated_value_pof ?? '',
        },
        {
          id: 'risk_environment_value_pof_c',
          value: selectedRiskFactorValues.risk_environment_calculated_value_pof ?? '',
        },
        {
          id: 'risk_public_impact_value_pof_c',
          value: selectedRiskFactorValues.risk_public_impact_calculated_value_pof ?? '',
        },
        {
          id: 'risk_direct_cost_value_pof_c',
          value: selectedRiskFactorValues.risk_direct_cost_calculated_value_pof ?? '',
        },
        {
          id: 'risk_business_loss_value_pof_c',
          value: selectedRiskFactorValues.risk_business_loss_calculated_value_pof ?? '',
        },
      ];
      const pofValues: { [key: string]: string } = {
        risk_safety_calculated_value_pof:
          selectedRiskFactorValues.risk_safety_calculated_value_pof ?? '',
        risk_environment_calculated_value_pof:
          selectedRiskFactorValues.risk_environment_calculated_value_pof ?? '',
        risk_public_impact_calculated_value_pof:
          selectedRiskFactorValues.risk_public_impact_calculated_value_pof ?? '',
        risk_direct_cost_calculated_value_pof:
          selectedRiskFactorValues.risk_direct_cost_calculated_value_pof ?? '',
        risk_business_loss_calculated_value_pof:
          selectedRiskFactorValues.risk_business_loss_calculated_value_pof ?? '',
      };
      const filteredInputValues = inputValues.filter((input) => input.value !== '');
      onChange({ inputs: filteredInputValues, pofValues });
    },
    [onChange, selectedValues]
  );

  return (
    <Stack spacing={3}>
      <Stack direction="row" gap={3} justifyContent="space-between" width="100%">
        <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
          <CustomField
            location={RiskFieldsTypeEnum.Pof.toString()}
            sx={textFieldStyles}
            fieldType={TemplateFieldTypeEnum.SingleSelect}
            fieldName="Facing Type"
            fieldOptions={[UHMW_FACING, TIMBER_FACING]}
            initialValue={UHMW_FACING}
            onChange={(value) => changeField(value, 'risk_facing_type')}
            commentIdentifier="risk_facing_type"
            placeholder="Enter value or will be taken from inspection"
          />
        </Stack>

        {selectedValues.risk_facing_type === UHMW_FACING && (
          <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
            <CustomField
              location={RiskFieldsTypeEnum.Pof.toString()}
              fieldType={TemplateFieldTypeEnum.SingleSelect}
              fieldName="Pad Thickness (<12mm)"
              fieldOptions={['Yes', 'No']}
              onChange={(value) => changeField(value, 'risk_pad_thickness')}
              commentIdentifier="risk_pad_thickness"
              initialValue=""
            />
          </Stack>
        )}

        {selectedValues.risk_facing_type === TIMBER_FACING && (
          <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
            <CustomField
              location={RiskFieldsTypeEnum.Pof.toString()}
              fieldType={TemplateFieldTypeEnum.SingleSelect}
              fieldName="Timber service time (years)"
              fieldOptions={['Yes', 'No']}
              onChange={(value) => changeField(value, 'risk_timber_service_time')}
              commentIdentifier="risk_timber_service_time"
              initialValue=""
            />
          </Stack>
        )}
      </Stack>

      {selectedValues.risk_facing_type === TIMBER_FACING && (
        <Stack direction="row" gap={3} justifyContent="space-between" width="100%">
          <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
            <CustomField
              location={RiskFieldsTypeEnum.Pof.toString()}
              fieldType={TemplateFieldTypeEnum.SingleSelect}
              fieldName="Cross-section loss in area (<40%)"
              fieldOptions={['Yes', 'No']}
              onChange={(value) => changeField(value, 'risk_cross_section_loss_in_area')}
              commentIdentifier="risk_cross_section_loss_in_area"
              initialValue=""
            />
          </Stack>
          <Stack direction="row" gap={3} alignItems="flex-end" width="100%">
            <CustomField
              location={RiskFieldsTypeEnum.Pof.toString()}
              fieldType={TemplateFieldTypeEnum.SingleSelect}
              fieldName="Cracking in timber fendering"
              fieldOptions={['Yes', 'No']}
              onChange={(value) => changeField(value, 'risk_cracking_in_timber_fendering')}
              commentIdentifier="risk_cracking_in_timber_fendering"
              initialValue=""
            />
          </Stack>
        </Stack>
      )}
      <RiskFactorInputTabs parentTab="pof" onInputChange={changeRiskFactor} />
    </Stack>
  );
};
